import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Text, useToast } from '@chakra-ui/react';

export type BluetoothScannerProps = {
  onScan: (data: string) => void;
  onConnectionChange?: (connected: boolean) => void;
};

export default function BluetoothScanner({ onScan, onConnectionChange }: BluetoothScannerProps) {
  const [device, setDevice] = useState<BluetoothDevice | null>(null);
  const [server, setServer] = useState<BluetoothRemoteGATTServer | null>(null);
  const [characteristic, setCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const bufferRef = useRef('');
  const toast = useToast();

  const handleCharacteristicValueChanged = useCallback(
    (event: Event) => {
      const target = event.target as unknown as BluetoothRemoteGATTCharacteristic;
      const value = new TextDecoder().decode(target.value as DataView);

      // Use ref instead of state to avoid stale closures
      bufferRef.current += value;

      if (bufferRef.current.includes('\r')) {
        const fullMessage = bufferRef.current.trim();
        onScan(fullMessage);
        bufferRef.current = '';
      }
    },
    [onScan],
  );

  const disconnect = useCallback(async () => {
    if (characteristic) {
      try {
        await characteristic.stopNotifications();
        characteristic.removeEventListener('characteristicvaluechanged', handleCharacteristicValueChanged);
      } catch (error) {
        console.error('Error stopping notifications:', error);
      }
    }

    if (server && server.connected) {
      try {
        server.disconnect();
      } catch (error) {
        console.error('Error disconnecting from server:', error);
      }
    }

    setDevice(null);
    setServer(null);
    setCharacteristic(null);
    bufferRef.current = '';
    setIsConnected(false);
    onConnectionChange?.(false);
  }, [characteristic, server, handleCharacteristicValueChanged, onConnectionChange]);

  const connectToDevice = async () => {
    setIsConnecting(true);
    try {
      if (!('bluetooth' in navigator)) {
        toast({
          title: 'Bluetooth API not supported',
          description: "Your browser doesn't support the Web Bluetooth API",
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error('Bluetooth API not supported.');
        return;
      }

      let bluetoothDevice;
      try {
        bluetoothDevice = await navigator.bluetooth?.requestDevice({
          // services: ['0000feea-0000-1000-8000-00805f9b34fb'] // UUID "Unknown service" from Tera HW0009 scanner - it was found via nRF Connect app
          // optionalServices: ['0000feea-0000-1000-8000-00805f9b34fb'] // UUID "Unknown service" from Tera HW0009 scanner - it was found via nRF Connect app
          filters: [{ services: ['0000feea-0000-1000-8000-00805f9b34fb'] }],
          optionalServices: ['battery_service'],
        });
      } catch (error) {
        // User cancelled the device selection
        console.log('User cancelled the device selection');
        return;
      }

      if (!bluetoothDevice) {
        toast({
          title: 'No device selected',
          description: 'No Bluetooth device was selected',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setDevice(bluetoothDevice);

      if (!bluetoothDevice.gatt) {
        throw new Error('Bluetooth device does not support GATT');
      }

      const gattServer = await bluetoothDevice.gatt.connect();
      if (!gattServer) {
        throw new Error('Failed to connect to GATT server');
      }
      setServer(gattServer);

      // // Im not sure if this is working.
      // const batteryService = await gattServer.getPrimaryService('battery_service');
      // console.log('Connected to service:', batteryService);
      // const batteryCharacteristic = await batteryService.getCharacteristic('battery_level');

      // const value = await batteryCharacteristic.readValue();
      // const batteryLevel = value.getUint8(0); // Poziom baterii w %
      // console.log(`Battery level: ${batteryLevel}%`);

      const service = await gattServer.getPrimaryService('0000feea-0000-1000-8000-00805f9b34fb');
      console.log('Connected to service:', service);
      const gattCharacteristic = await service.getCharacteristic('00002aa1-0000-1000-8000-00805f9b34fb');
      console.log('Characteristic UUID:', characteristic);
      setCharacteristic(gattCharacteristic);

      await gattCharacteristic.startNotifications();
      gattCharacteristic.addEventListener('characteristicvaluechanged', handleCharacteristicValueChanged);

      setIsConnected(true);
      onConnectionChange?.(true);
      toast({
        title: 'Connected',
        description: `Connected to ${bluetoothDevice.name || 'Bluetooth scanner'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error connecting to Bluetooth device:', error);
      toast({
        title: 'Connection failed',
        description: `Failed to connect: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsConnected(false);
      onConnectionChange?.(false);
    } finally {
      setIsConnecting(false);
    }
  };

  // useEffect(() => {
  //   return () => {
  //     disconnect();
  //   };
  // }, [disconnect]);

  return (
    <Box>
      {!isConnected ? (
        <Button colorScheme="blue" onClick={connectToDevice} isLoading={isConnecting} loadingText="Connecting...">
          Connect Bluetooth Scanner
        </Button>
      ) : (
        <Box>
          <Text mb={2} color="green.500">
            Connected to: {device?.name || 'Bluetooth scanner'}
          </Text>
          <Button colorScheme="red" onClick={disconnect}>
            Disconnect Bluetooth Scanner
          </Button>
        </Box>
      )}
    </Box>
  );
}
